/**
 * Numbered pagination + page-size selector for the Originator redesign.
 * Purely presentational; the parent owns page/pageSize state and data fetching.
 */
const PAGE_SIZES = [10, 20, 50];

function pageWindow(page, totalPages) {
  const pages = [];
  const start = Math.max(1, Math.min(page - 1, totalPages - 2));
  const end = Math.min(totalPages, start + 2);
  for (let p = start; p <= end; p += 1) pages.push(p);
  return pages;
}

export function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizes = PAGE_SIZES,
  disabled = false,
}) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pages = pageWindow(page, totalPages);
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);

  const navBtn =
    'inline-flex h-8 min-w-8 items-center justify-center rounded-lg border border-eorig-border bg-white px-2 text-xs font-semibold text-eorig-ink transition-colors hover:bg-eorig-surface disabled:cursor-not-allowed disabled:opacity-40';

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-2 text-xs text-eorig-muted">
        <span>
          {from}–{to} of {total}
        </span>
        {onPageSizeChange ? (
          <label className="ml-2 flex items-center gap-1.5">
            <span>Rows</span>
            <select
              value={pageSize}
              disabled={disabled}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="rounded-lg border border-eorig-border bg-white px-2 py-1 text-xs text-eorig-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-eorig-cyan/35"
            >
              {pageSizes.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </div>

      <div className="flex items-center gap-1.5">
        <button
          type="button"
          className={navBtn}
          onClick={() => onPageChange(Math.max(1, page - 1))}
          disabled={disabled || page <= 1}
        >
          Prev
        </button>
        {pages[0] > 1 ? (
          <span className="px-1 text-xs text-eorig-muted">…</span>
        ) : null}
        {pages.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => onPageChange(p)}
            disabled={disabled}
            aria-current={p === page ? 'page' : undefined}
            className={`inline-flex h-8 min-w-8 items-center justify-center rounded-lg px-2 text-xs font-semibold transition-colors ${
              p === page
                ? 'bg-eorig-navy-600 text-white'
                : 'border border-eorig-border bg-white text-eorig-ink hover:bg-eorig-surface'
            }`}
          >
            {p}
          </button>
        ))}
        {pages[pages.length - 1] < totalPages ? (
          <span className="px-1 text-xs text-eorig-muted">…</span>
        ) : null}
        <button
          type="button"
          className={navBtn}
          onClick={() => onPageChange(Math.min(totalPages, page + 1))}
          disabled={disabled || page >= totalPages}
        >
          Next
        </button>
      </div>
    </div>
  );
}
